"use client";

import Link from "next/link";
import { ArrowRight, CheckCircle2, Circle, Sparkles } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { technology as data } from "@/lib/data/about";

export function Technology() {
  return (
    <Section id="technology" className="relative">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-1/3 left-1/4 h-80 w-80 rounded-full bg-[var(--color-denim)] opacity-15 blur-[120px]" />
      </div>

      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny text-[var(--color-denim)]">{data.eyebrow}</span>
            <h2 className="mt-3">{data.title}</h2>
            <p className="mt-5 text-body text-[rgb(var(--fg-muted))] leading-relaxed">{data.intro}</p>
          </div>
        </Reveal>

        <div className="mt-12 grid lg:grid-cols-3 gap-5">
          {data.whyProprietary.map((row, i) => (
            <Reveal key={row.heading} delay={i * 80}>
              <div className="glass rounded-2xl p-6 h-full">
                <div className="eyebrow-tiny text-[var(--color-denim)]">0{i + 1}</div>
                <h3 className="mt-2 text-lg">{row.heading}</h3>
                <p className="mt-3 text-sm text-[rgb(var(--fg-muted))] leading-relaxed">{row.body}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-12 grid lg:grid-cols-2 gap-6">
          <Reveal>
            <div className="glass rounded-2xl p-6">
              <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))]">Intellectual Property</div>
              <div className="mt-5 space-y-4">
                {data.intellectualProperty.map((ip) => (
                  <div key={ip.label} className="flex items-baseline gap-4">
                    <div className="text-2xl font-bold text-[var(--color-crimson)] shrink-0 w-16">{ip.value}</div>
                    <div>
                      <div className="font-semibold text-sm">{ip.label}</div>
                      <div className="text-xs text-[rgb(var(--fg-muted))] mt-0.5">{ip.detail}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className="glass rounded-2xl p-6 h-full">
              <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))]">R&amp;D Pipeline</div>
              <div className="mt-5 space-y-4">
                {data.rdPipeline.map((item, i) => {
                  const Icon = item.status === "shipping" ? CheckCircle2 : item.status === "next" ? Sparkles : Circle;
                  const accent =
                    item.status === "shipping"
                      ? "var(--color-emerald)"
                      : item.status === "next"
                        ? "var(--color-amber)"
                        : "var(--color-cyan)";
                  return (
                    <div key={item.phase} className="flex items-start gap-3">
                      <span
                        className="grid place-items-center h-9 w-9 rounded-lg shrink-0"
                        style={{
                          background: `color-mix(in oklch, ${accent} 18%, transparent)`,
                          boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 35%, transparent)`,
                        }}
                      >
                        <Icon className="h-4 w-4" style={{ color: accent }} />
                      </span>
                      <div>
                        <div className="eyebrow-tiny" style={{ color: accent }}>
                          {item.phase}
                        </div>
                        <div className="text-xs text-[rgb(var(--fg-muted))] mt-0.5 leading-relaxed">{item.description}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={300}>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            {data.cta.map((c, i) => (
              <Link
                key={c.label}
                href={c.href}
                className={
                  i === 0
                    ? "btn-primary-gradient h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"
                    : "btn-glass h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"
                }
              >
                {c.label} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ))}
          </div>
        </Reveal>
      </Container>
    </Section>
  );
}
